import { RESTClient } from './client';
import { API_URL } from './apiDictionary';
import { PaginatedResponse } from '@dynamika-shared/types/general';
import { ClinicalTrialType } from '@dynamika-shared/types/clinicalTrials';
import {
  SiteSubjectType,
  ExternalUserFile,
  FileDownloadInfo,
  ExternalUserPatientDetails,
  ExternalUserPatientVisitsResponse,
  ClinicalTrialContext,
} from '@restore/ExternalUserSubjects/ExternalUserSubjects.types';
import { prepareUrl } from '@utils/routerHelpers';

/**
 * API module for External User endpoints
 */

type FetchSubjectsArgs = {
  patientCode?: string;
  page?: number;
  size?: number;
  sort?: string[];
};

/**
 * Fetch clinical trials available for external user
 * GET /clinical-trials/affiliations/by-role?role=EXTERNAL_USER
 */
export const fetchExternalUserTrials = async (role: string = 'EXTERNAL_USER') => {
  const response = await RESTClient.get<PaginatedResponse<ClinicalTrialType>>(
    API_URL.clinicalTrialsAffiliations,
    {
      params: { role },
    }
  );

  return response.data;
};

/**
 * Fetch subjects list for external user
 * GET /external-user/patients
 * Header: x-dynamika-clinical-trial-id
 */
export const fetchExternalUserSubjects = async (
  clinicalTrialId: string,
  args?: FetchSubjectsArgs
) => {
  const response = await RESTClient.get<PaginatedResponse<SiteSubjectType>>(
    API_URL.externalUserPatients,
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      params: {
        patientCode: args?.patientCode || undefined,
        page: args?.page ? args?.page - 1 : 0,
        size: args?.size || 10,
        sort: args?.sort || [],
      },
    }
  );

  return response.data;
};

/**
 * Fetch clinical trial context including sites
 * GET /clinical-trials/context
 */
export const fetchClinicalTrialContext = async (
  clinicalTrialId: string
): Promise<ClinicalTrialContext> => {
  const response = await RESTClient.get<{
    configuration: { name: string; sites: { id: string; name: string; code?: string }[] };
  }>(API_URL.clinicalTrialContext, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
  });

  // Map API response to ClinicalTrialContext format
  return {
    clinicalTrialId,
    clinicalTrialName: response.data.configuration?.name,
    sites: response.data.configuration?.sites || [],
  };
};

/**
 * Fetch subject details (new API)
 * GET /external-user/patients/{patientId}
 * Header: x-dynamika-clinical-trial-id
 * Returns: { id, dob, status, code, siteName, cohortName }
 */
export const fetchExternalUserSubjectDetails = async (
  clinicalTrialId: string,
  patientId: string
) => {
  const url = prepareUrl(API_URL.externalUserPatientDetails, [patientId]);
  const response = await RESTClient.get<ExternalUserPatientDetails>(url, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
  });

  return response.data;
};

/**
 * Fetch patient visits (new API)
 * GET /external-user/patients/{patientId}/visits
 * Header: x-dynamika-clinical-trial-id
 * Returns: { clinicalTrialId, siteId, patientId, visits[] }
 */
export const fetchExternalUserPatientVisits = async (
  clinicalTrialId: string,
  patientId: string
) => {
  const url = prepareUrl(API_URL.externalUserPatientVisits, [patientId]);
  const response = await RESTClient.get<ExternalUserPatientVisitsResponse>(url, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
  });

  return response.data;
};

/**
 * Fetch patient files
 * GET /external-user/patients/{patientId}/files
 * Header: x-dynamika-clinical-trial-id
 */
export const fetchExternalUserPatientFiles = async (
  clinicalTrialId: string,
  patientId: string
) => {
  const url = prepareUrl(API_URL.externalUserPatientFiles, [patientId]);
  const response = await RESTClient.get<ExternalUserFile[]>(url, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
  });

  return response.data;
};

/**
 * Get file download URL
 * GET /external-user/files/{fileId}/download
 * Header: x-dynamika-clinical-trial-id
 */
export const getExternalUserFileDownload = async (
  clinicalTrialId: string,
  fileId: string
) => {
  const url = prepareUrl(API_URL.externalUserFileDownload, [fileId]);
  const response = await RESTClient.get<FileDownloadInfo>(url, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
  });

  return response.data;
};

